import PropTypes from 'prop-types';
import React, { useEffect, useContext, useState, useRef, useMemo } from 'react';

import {
  CHOOSING_TICKET_TYPES,
  CHOOSING_ADD_ONS,
  CHOOSING_DONATION,
  CHOOSING_MEMBERSHIP
} from '../constants/webOrderFormConstants';

var _ = require('lodash');

import { ToastContainer, toast } from 'react-toastify';
import {loadStripe} from '@stripe/stripe-js';
import { ActionCableContext } from '../startup/WebOrderFormApp';
import { TicketAvailabilityCalculator } from '../../TicketAvailability/TicketAvailabilityCalculator';
import { activeRevealedHold } from '../actions/webOrderFormActionCreators';
import applyWidgetSettings from '../utils/applyWidgetSettings';

import ConnectionLost from './ConnectionLost';
import SoldOut from './SoldOut';
import Canceled from './Canceled';
import TimeLimitReached from './TimeLimitReached';
import PickTickets from './PickTickets';
import Checkout from './Checkout';
import { channelsForEvent } from '../../../shared/channelsByEvent';
import { addOnQuantityOptions } from '../utils/addOnQuantityOptions';
import { visibleTicketTypes } from '../utils/visibleTicketTypes';
import { visibleAddOns } from '../utils/visibleAddOns';
import { soldOutAddOns } from '../utils/soldOutAddOns';
import { withHeldAddOns } from '../utils/heldAddOns';
import { acceptsPostedVisitor } from '../../../shared/attribution/useAttributionVisitor';
import { keepSeatSelections } from '../utils/seatSelection';
import useLinkAttributionVisitor from '../utils/useLinkAttributionVisitor';

const axios = require('axios').default;

const https = require('https');
const httpsAgent = new https.Agent({ keepAlive: true });

var formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});


const notPublishedYet = (confirm) => {
  if(confirm.published_at === null){
    return true;
  }

  var publishedAt = new Date(confirm.published_at);
  var now = new Date();

  return now < publishedAt;
}

export const showSoldOutScreen = (confirm, ticketReservation, ticketTypes, resaleListings) => {
  return (
    confirm.sold_out &&
      !ticketReservation.id &&
      choosingTicketTypes(ticketReservation) &&
      ticketTypes.filter(tt => !tt.manifested && tt.visibility !== "Hidden").length === 0 &&
      (resaleListings || []).length === 0
  );
}

export const showSaleEndedScreen = (confirm, ticketReservation, resaleListings, now = new Date()) => {
  if (ticketReservation.id || !confirm.sales_end_at || new Date(confirm.sales_end_at) >= now) {
    return false;
  }

  const showOver = !!confirm.end_time && new Date(confirm.end_time) <= now;
  return showOver || (resaleListings || []).length === 0;
}

export const postMessageReceived = (
  event,
  gtag,
  confirm,
  customerioIdChanged,
  parentUrlChanged,
  reportParentUrlForUtmBeacon,
  originAttributionChanged,
  metaClickIdChanged,
  goToAddOns,
  ticketReservation,
  postToParent,
  ticketReservationChanged,
  nextStepIsChooseAddOns,
  visibleAddOns,
  checkoutCategories,
  requestedCheckoutCategoryIdChanged,
  attributionVisitorChanged
) => {
  var payload = event.data;

  if(typeof(payload) === "object" && payload.from === "od_embed.js"){
    var command = payload.command;
    var data = payload.data;

    switch (command) {
      case "triggerGoToAddOns":
        if(nextStepIsChooseAddOns(ticketReservation, visibleAddOns, checkoutCategories)){
          goToAddOns(ticketReservation, postToParent, ticketReservationChanged);
        }
        break;
      case "setClientID":
        gtag('set', {'client_id': data.clientId});
        break;
      case "setCustomerioId":
        customerioIdChanged(data.customerioId);
        break;
      case "setParentUrl":
        parentUrlChanged(data.parentUrl);
        reportParentUrlForUtmBeacon(confirm.id);
        break;
      case "setOriginAttribution":
        originAttributionChanged(data);
        break;
      case "setMetaClickId":
        metaClickIdChanged(data.fbclid);
        break;
      case "setAttributionVisitor":
        if(attributionVisitorChanged && data && data.visitorUuid && acceptsPostedVisitor() && (event.source === window.parent || event.source === window)){
          attributionVisitorChanged(data.visitorUuid);
        }
        break;
      case "setCheckoutCategory":
        requestedCheckoutCategoryIdChanged(data ? data.checkoutCategoryId : null);
        break;
      case "reportPageView":
        gtag('event', 'page_view', {
          page_title: confirm.title,
          page_location: "/confirms/" + confirm.id + "/web_orders/new"
        });

        postToParent("ga.page_view", "/confirms/" + confirm.id + "/web_orders/new");
        break;
      default:
        break;
    }
  }
}

const postToParent = (command, data) => {
  parent.postMessage({
    from: "WebOrderForm.jsx",
    command: command,
    data: data
  }, "*");
}

const formatItemsForGoogleTagManager = (confirm, promoCode, ticketTypes, addOns) => {
  var formattedticketTypes = ticketTypes
    .filter((tt) => tt.quantity && tt.quantity > 0)
    .map((tt) => {
      return {
        item_name: confirm.id + " " + tt.name,
        item_id: tt.id,
        price: parseFloat(tt.calculated_price),
        quantity: tt.quantity
      }
    });

  var formattedAddOns = addOns
    .filter((ao) => ao.quantity && ao.quantity > 0)
    .map((ao) => {
      return {
        item_name: confirm.id + " " + ao.name,
        item_id: ao.id,
        price: parseFloat(ao.calculated_price),
        quantity: ao.quantity
      }
    });

  return formattedticketTypes
    .concat(formattedAddOns);
}

const choosingTicketTypes = (ticketReservation) => {
  return (
    !ticketReservation._screen
      || ticketReservation._screen === CHOOSING_TICKET_TYPES
  );
}

const choosingAddOns = (ticketReservation) => {
  return (
    ticketReservation._screen
      && ticketReservation._screen === CHOOSING_ADD_ONS
  );
}

const nextStepIsChooseAddOns = (ticketReservation, visibleAddOns, checkoutCategories) => {
  return (
    visibleAddOns.length > 0
      && checkoutCategories.length === 0
      && (
        Object.keys(ticketReservation).length === 0
          || choosingTicketTypes(ticketReservation)
      )
  );
}

const hasDonationStep = (donationCustomChargeType, confirm, userContext, ticketTypes, startAtDonation) => {
  var hasAdmissionTicketsInCart = (
    ticketTypes.some((tt) => tt.quantity && tt.quantity > 0)
      || (userContext.tickets || []).some((t) => t.ticket_type_id)
  );

  var meetsTicketTypeRequirement = (
    !confirm.donation_requires_ticket_type
      || hasAdmissionTicketsInCart
  );

  var canBypassTicketRequirement = (
    confirm.disable_donation_requires_ticket_type_with_direct_link
      && startAtDonation
  );

  return (
    donationCustomChargeType
      && donationCustomChargeType.id
      && (meetsTicketTypeRequirement || canBypassTicketRequirement)
  );
}

const nextStepIsChooseDonation = (ticketReservation, visibleAddOns, donationCustomChargeType, checkoutCategories, confirm, userContext, ticketTypes, startAtDonation) => {
  var goToDonationFromTicketTypes = (
    choosingTicketTypes(ticketReservation)
      && !nextStepIsChooseAddOns(ticketReservation, visibleAddOns, checkoutCategories)
  );

  var goToDonationFromAddOns = (
    choosingAddOns(ticketReservation)
  );

  return (
    hasDonationStep(donationCustomChargeType, confirm, userContext, ticketTypes, startAtDonation)
      && (goToDonationFromTicketTypes || goToDonationFromAddOns)
      && checkoutCategories.length === 0
  );
}

const pushScreenHistory = (ticketReservation) =>
  [...(ticketReservation._screenHistory || []), ticketReservation._screen || CHOOSING_TICKET_TYPES];

const goBack = (ticketReservation, ticketReservationChanged) => {
  const history = ticketReservation._screenHistory || [];
  if (history.length === 0) { return; }
  const previous = history[history.length - 1];
  ticketReservationChanged(Object.assign({}, ticketReservation, {
    _screen: previous,
    _screenHistory: history.slice(0, -1)
  }));
}

export const goToAddOns = (ticketReservation, postToParent, ticketReservationChanged, withoutTickets = false) => {
  var updated = Object.assign({}, ticketReservation, {_screen: CHOOSING_ADD_ONS, _screenHistory: withoutTickets ? [] : pushScreenHistory(ticketReservation)});
  postToParent("opendateOrderGoToAddOns", {});
  ticketReservationChanged(updated);
}

const goToDonation = (ticketReservation, ticketReservationChanged) => {
  var updated = Object.assign({}, ticketReservation, {_screen: CHOOSING_DONATION, _screenHistory: pushScreenHistory(ticketReservation)});
  ticketReservationChanged(updated);
}

const nextStepIsChooseMembership = (ticketReservation, visibleAddOns, donationCustomChargeType, checkoutCategories, confirm, userContext, ticketTypes, membershipProducts, startAtDonation) => {
  if (membershipProducts.length === 0 || checkoutCategories.length > 0) {
    return false;
  }

  const goFromTicketTypes = (
    choosingTicketTypes(ticketReservation)
      && !nextStepIsChooseAddOns(ticketReservation, visibleAddOns, checkoutCategories)
      && !hasDonationStep(donationCustomChargeType, confirm, userContext, ticketTypes, startAtDonation)
  );

  const goFromAddOns = (
    choosingAddOns(ticketReservation)
      && !hasDonationStep(donationCustomChargeType, confirm, userContext, ticketTypes, startAtDonation)
  );

  const goFromDonation = (
    ticketReservation._screen === CHOOSING_DONATION
  );

  return goFromTicketTypes || goFromAddOns || goFromDonation;
}

const goToMembership = (ticketReservation, ticketReservationChanged) => {
  const updated = Object.assign({}, ticketReservation, {_screen: CHOOSING_MEMBERSHIP, _screenHistory: pushScreenHistory(ticketReservation)});
  ticketReservationChanged(updated);
}

var debouncedBuildTicketReservation;

export const ticketTypeAvailable = (ticketType) => {
  return !(
    ticketType.sold_out || ticketType.sales_ended || ticketType.scheduled || ticketType.sales_paused
  );
};

export const salesStartInFuture = (confirm) => {
  return confirm.sales_start_at
    && new Date(confirm.sales_start_at) > new Date();
};

const WebOrderForm = ({
  team,
  csrfToken,
  confirm,
  confirmChanged,
  channelMsgReceived,
  ticketTypes,
  promoCode,
  lookupPromoCode,
  promoCodeName,
  promoCodeNameChanged,
  promoCodeNameValid,
  promoCodeInvalidReason,
  promoCodeChanged,
  ticketReservation,
  ticketTypesChanged,
  buildTicketReservation,
  ticketReservationChanged,
  createTicketReservation,
  deleteTicketReservation,
  stripePromise,
  stripePromiseChanged,
  stripePublishableKey,
  stripeConnectedAccountId,
  stripeError,
  stripeErrorChanged,
  updateTicketReservation,
  rootUrl,
  checkoutTimePercentageRemaining,
  checkoutTimePercentageRemainingChanged,
  timeLimitReached,
  timeLimitReachedChanged,
  isPlacingOrder,
  isPlacingOrderChanged,
  addOns,
  addOnsChanged,
  googleTrackingIds,
  gtag,
  gtagChanged,
  customFieldValues,
  customFieldValueChanged,
  userContext,
  cancelCheckout,
  seatsIOPublicKey,
  seatsIOChart,
  seatsIOChartChanged,
  seatsIOSelectedObjects,
  seatsIOSelectedObjectsChanged,
  autoSaveTicketReservation,
  customerioId,
  customerioIdChanged,
  parentUrl,
  parentUrlChanged,
  reportParentUrlForUtmBeacon,
  originAttributionChanged,
  metaClickIdChanged,
  attributionVisitorChanged,
  attributionVisitorUuid,
  isCreatingTicketReservation,
  cartStartBusyMessage,
  cartStartBusyMessageChanged,
  saveGeoCodedStateCode,
  verticalInsureClientID,
  registerTicketInsurance,
  insuranceOfferState,
  insuranceOfferStateChanged,
  ticketInsurance,
  deleteTicketInsurance,
  hasInsuranceQuote,
  hasInsuranceQuoteChanged,
  announceListEnabled,
  waitlistEnabled,
  donationCustomChargeType,
  donationCustomChargeTypeChanged,
  feeRules,
  startAtAddOns,
  ticketsSoldElsewhere,
  startAtDonation,
  showHeader,
  showCheckoutDate,
  checkoutCategories,
  restrictedTicketTypeId,
  restrictedTicketGroupId,
  entryTimeLabel,
  seriesReturnUrl,
  onRestart,
  ticketGroups,
  ticketGroupsChanged,
  revealedHold,
  verb,
  userContextChanged,
  membershipProducts,
  selectedMembershipProduct,
  fetchMembershipProducts,
  fetchMembershipPromoCode,
  fetchPassPromoCode,
  selectedMembershipProductChanged,
  widgetConfig,
  widgetType,
  adminPreview,
  useDefaultTheme,
  widgetTemplateId,
  resaleListings,
  resaleListingsFetched,
  resaleOrderItems,
  resaleQuantityChanged,
  clearPromoRevealedTiers,
}) => {
  // The Custom theme is designed for embedding on customers' own websites.
  // When Opendate renders the iframe on its own /e/<slug> page, useDefaultTheme
  // is true and we keep the Custom structure but skip the customer-styled
  // theme so it doesn't fight Opendate's page chrome.
  const themeApplies = widgetType === 'custom' && !useDefaultTheme;
  const widgetStyle = themeApplies ? applyWidgetSettings(widgetConfig) : undefined;
  const [ticketTypesLoading, setTicketTypesLoading] = useState(true);
  const [connectionLost, setConnectionLost] = useState(false);
  const [requestedCheckoutCategoryId, setRequestedCheckoutCategoryId] = useState(null);
  const cachedVisibleAddOns = adminPreview
    ? addOns
    : visibleAddOns(addOns, ticketTypes, userContext, startAtAddOns);
  const cachedSoldOutAddOns = soldOutAddOns(addOns, cachedVisibleAddOns, checkoutCategories);
  const boundVisibleTicketTypes = useMemo(
    () => (confirm, ticketTypes, promoCode) =>
      visibleTicketTypes(confirm, ticketTypes, promoCode, restrictedTicketTypeId, restrictedTicketGroupId),
    [restrictedTicketTypeId, restrictedTicketGroupId]
  );
  const idleCheckInterval = useRef(null);
  const confirmSubscription = useRef(null);
  const messageListeners = useRef([]);
  const calculatorRef = useRef(null);
  const pendingSaleWindowsRef = useRef(null);
  const ticketTypesRef = useRef(ticketTypes);
  ticketTypesRef.current = ticketTypes;

  useLinkAttributionVisitor({ confirmId: confirm.id, userContext, attributionVisitorUuid });

  useEffect(() => () => {
    clearInterval(idleCheckInterval.current);
    confirmSubscription.current?.unsubscribe();
    messageListeners.current.forEach((listener) => window.removeEventListener("message", listener, false));
  }, []);
  const useRealTimeData = team.real_time_ticket_data && !confirm.disable_real_time_ticket_data;
  const cable = useRealTimeData ? useContext(ActionCableContext).cable : null;
  const channels = channelsForEvent(confirm.seatsio_event_id, promoCode);
  const seatedHoldPresent = channels.length > 1;

  const unlockedHold = activeRevealedHold(revealedHold, promoCode);

  useEffect(() => {
    const current = calculatorRef.current?.revealedHold;
    if (!calculatorRef.current || (current?.id === unlockedHold.id && !!current?.capped_by_hold === !!unlockedHold.capped_by_hold)) {
      return;
    }

    calculatorRef.current.setRevealedHold(unlockedHold);
    ticketTypesChanged(keepSeatSelections(calculatorRef.current.calculate().ticketTypes, ticketTypesRef.current));
  }, [unlockedHold.id, unlockedHold.capped_by_hold]);

  useEffect(() => {
    const next = withHeldAddOns(addOns, unlockedHold);
    if (next.some((addOn, index) => addOn !== addOns[index])) {
      addOnsChanged(next);
    }
  }, [addOns, unlockedHold.id, unlockedHold.capped_by_hold]);

  const loadTicketTypes = (context) => {
    const params = context && context.access_token ? { ucat: context.access_token } : {};
    return axios.get(`/confirms/${confirm.id}/web_orders/available_tickets`, { params })
      .then(({ data }) => {
        calculatorRef.current = new TicketAvailabilityCalculator({
          groups: data.ticketGroupAvailability,
          ticketTypes: data.ticketTypes,
          revealedHold: unlockedHold,
        });

        if (pendingSaleWindowsRef.current) {
          calculatorRef.current.updateSaleWindows(pendingSaleWindowsRef.current);
          pendingSaleWindowsRef.current = null;
        }

        const result = calculatorRef.current.calculate();
        
        ticketTypesChanged(result.ticketTypes);
        ticketGroupsChanged(data.ticketGroups);
        resaleListingsFetched(data.resaleListings || []);
        
        const allTicketTypesSoldOut = result.ticketTypes.length > 0 && 
          result.ticketTypes.every(tt => tt.sold_out);
        
        confirmChanged({
          ...confirm,
          sold_out: allTicketTypesSoldOut && !seatedHoldPresent,
          seated_hold_present: seatedHoldPresent
        });
        
        setTicketTypesLoading(false);
      });
  };

  const refreshSaleWindows = (context) => {
    axios.get(`/confirms/${confirm.id}/web_orders/available_tickets`, { params: { ucat: context.access_token } })
      .then(({ data }) => {
        if (!calculatorRef.current) {
          pendingSaleWindowsRef.current = data.ticketTypes;
          return;
        }
        calculatorRef.current.updateSaleWindows(data.ticketTypes);
        ticketTypesChanged(keepSeatSelections(calculatorRef.current.calculate().ticketTypes, ticketTypesRef.current));
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchMembershipProducts(confirm, userContext);
    loadTicketTypes(userContext);
  }, []);

  useEffect(() => {
    fetchMembershipPromoCode(confirm, userContext, selectedMembershipProduct, calculatorRef);
  }, [selectedMembershipProduct?.id]);

  // O-3930 WS10. Auto-reveal a logged-in pack owner's hidden $0 pack ticket on
  // load (and whenever they log in), mirroring the membership reveal above.
  useEffect(() => {
    fetchPassPromoCode(confirm, userContext, calculatorRef);
  }, [userContext?.access_token]);

  useEffect(() => {
    if (!ticketTypesLoading && promoCodeName && !promoCode?.id) {
      lookupPromoCode(confirm, promoCodeName, ticketTypes, csrfToken, addOns, ticketReservation, userContext);
    }
  }, [ticketTypesLoading]);

  useEffect(() => {
    if(!cable) { return; }

    if (idleCheckInterval.current) {
      clearInterval(idleCheckInterval.current);
    }

    idleCheckInterval.current = setInterval(() => {
      if (!ticketReservation.id) {
        // A socket already waiting to reconnect fires no disconnected callback,
        // so end the session here rather than rely on it.
        setConnectionLost(true);
        confirmSubscription.current?.unsubscribe();
        cable.disconnect();
        clearInterval(idleCheckInterval.current);
        idleCheckInterval.current = null;
      }
    }, 5 * 60 * 1000);
  }, [ticketReservation]);

  useEffect(() => {
    if (cable && !ticketTypesLoading) {
      const receivedHandler = channelMsgReceived(calculatorRef);
      confirmSubscription.current = cable.subscriptions.create(
        {
          channel: "ConfirmChannel",
          id: confirm.id
        },
        {
          received: receivedHandler,
          // Deploys and backgrounded phone tabs drop the socket and ActionCable
          // retries; only a deliberate disconnect (the idle timer) ends the session.
          disconnected: ({ willAttemptReconnect } = {}) => {
            if (!willAttemptReconnect) { setConnectionLost(true); }
          }
        }
      )
    }

    var promise = stripeConnectedAccountId
      ? loadStripe(stripePublishableKey, { stripeAccount: stripeConnectedAccountId })
      : loadStripe(stripePublishableKey);
    stripePromiseChanged(promise);

    var gtag = () => {};

    googleTrackingIds.forEach(googleTrackingId => {
      window.dataLayer = window.dataLayer || [];
      gtag = function(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', googleTrackingId, {cookie_flags: 'secure;samesite=none'});
    });

    var wrappedPostMessageReceived = (event) => {
      postMessageReceived(
        event,
        gtag,
        confirm,
        customerioIdChanged,
        parentUrlChanged,
        reportParentUrlForUtmBeacon,
        originAttributionChanged,
        metaClickIdChanged,
        goToAddOns,
        ticketReservation,
        postToParent,
        ticketReservationChanged,
        nextStepIsChooseAddOns,
        cachedVisibleAddOns,
        checkoutCategories,
        setRequestedCheckoutCategoryId,
        attributionVisitorChanged
      );
    }

    if (!adminPreview) {
      if (window.addEventListener) {
        window.addEventListener("message", wrappedPostMessageReceived, false);
        messageListeners.current.push(wrappedPostMessageReceived);
      } else {
        window.attachEvent("onmessage", wrappedPostMessageReceived);
      }
    }

    gtagChanged(gtag);

    debouncedBuildTicketReservation = _.debounce((csrfToken, confirm, ticketTypes, promoCode, addOns, ticketReservation, userContext) => {
      buildTicketReservation(
        csrfToken,
        confirm,
        ticketTypes,
        promoCode,
        addOns,
        ticketReservation,
        userContext
      );
    }, 300);

    if(startAtAddOns && nextStepIsChooseAddOns(ticketReservation, visibleAddOns, checkoutCategories)){
      goToAddOns(ticketReservation, postToParent, ticketReservationChanged, ticketsSoldElsewhere);
    }

    if(startAtDonation && hasDonationStep(donationCustomChargeType, confirm, userContext, ticketTypes, startAtDonation)){
      goToDonation(ticketReservation, ticketReservationChanged);
    }
  }, [ticketTypesLoading]);

  const freeHoldTokens = () => {
    var orderItems = ticketTypes.filter((tt) =>
      tt.quantity && tt.quantity > 0
    );

    var addOnOrderItems = addOns.filter((ao) =>
      ao.quantity && ao.quantity > 0
    );

    var seatsioHoldTokens = [];

    if(orderItems.some((i) => i.seatsIOObjects && i.seatsIOObjects.length > 0)){
      seatsioHoldTokens = [...seatsioHoldTokens, seatsIOChart.holdToken];
    }

    var addOnHoldTokens = [
      ...new Set(
        addOnOrderItems
          .filter((item) => item.seatsIOObjects)
          .map((item) => item.seatsIOObjects)
          .flat()
          .map((object) => object.hold_token)
      )
    ];

    seatsioHoldTokens = [
      ...seatsioHoldTokens,
      ...addOnHoldTokens
    ];

    if (seatsioHoldTokens.length) {
      axios.get(`/confirms/${confirm.id}/ticket_reservations/expire_hold_tokens`, {
        params: {
          hold_tokens: seatsioHoldTokens
        },
        httpsAgent: httpsAgent
      })
      .catch(_error => {});
    }
  };

  useEffect(() => {
    if(adminPreview) return undefined;

    window.onbeforeunload = function(e) {
      var ticketTypesWithQuantity = ticketTypes.filter((tt) =>
        tt.quantity && tt.quantity > 0
      );

      var addOnsWithQuantity = addOns.filter((ao) =>
        ao.quantity && ao.quantity > 0
      );

      var shouldPromptOnPageUnload = (
        !isPlacingOrder
          && (
            ticketTypesWithQuantity.length > 0
              || addOnsWithQuantity.length > 0
          )
      );

      if(shouldPromptOnPageUnload) {
        freeHoldTokens();
        e.preventDefault();
        return "";
      }
    }

    return () => { window.onbeforeunload = null; };
  }, [ticketTypes, addOns, isPlacingOrder, adminPreview]);

  const findTicketGroup = (ticketGroupId, group) => {
    if (group.id === ticketGroupId) {
      return group;
    }

    for (const subGroup of (group.sub_groups || [])) {
      const found = findTicketGroup(ticketGroupId, subGroup);
      if (found) {
        return found;
      }
    }

    return null;
  }

  const updateTicketTypeQuantity = ({ ticketTypeId, quantity }) => {
    calculatorRef.current.updateCart({ ticketTypeId, quantity });
    const result = calculatorRef.current.calculate();
    ticketTypesChanged(keepSeatSelections(result.ticketTypes, ticketTypes));
  };

  const handleResaleQuantityChanged = (listingId, quantity, ticketTypeName, listPriceCents, buyerTotalPerTicketCents) => {
    resaleQuantityChanged(listingId, quantity, ticketTypeName, listPriceCents, buyerTotalPerTicketCents, calculatorRef);
  };

  const restoreOwnerPromoCodes = () => {
    fetchMembershipPromoCode(confirm, userContext, selectedMembershipProduct, calculatorRef);
    fetchPassPromoCode(confirm, userContext, calculatorRef);
  };

  const handleCancelCheckout = () => {
    freeHoldTokens();
    cancelCheckout();
    restoreOwnerPromoCodes();
  }

  const handleAuthSuccess = (authData) => {
    if (authData && authData.userContext) {
      userContextChanged(authData.userContext);
      fetchMembershipProducts(confirm, authData.userContext);
      refreshSaleWindows(authData.userContext);
      fetchMembershipPromoCode(confirm, authData.userContext, selectedMembershipProduct, calculatorRef);
      fetchPassPromoCode(confirm, authData.userContext, calculatorRef);
    }
  };

  return (
    <div className={themeApplies ? 'widget-custom' : 'container-fluid'} style={widgetStyle}>
      <ToastContainer icon={false} />
      {notPublishedYet(confirm) && !adminPreview ? (
        <div></div>
      ) : confirm.calendar_classification === "canceled" && !adminPreview ? (
        <Canceled />
      ) : !ticketTypesLoading && showSaleEndedScreen(confirm, ticketReservation, resaleListings) && !adminPreview ? (
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-8 offset-lg-2">
              <div className="card border-0 h-100" style={{"background": "#f9f9f9"}}>
                <div className="card-body d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <p className="mb-2"
                      style={{"fontSize": "16px"}}>
                      <strong>Sale ended</strong>
                    </p>
                    <p className="mb-0">
                      This event has ended and tickets are no longer available.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : showSoldOutScreen(confirm, ticketReservation, ticketTypes, resaleListings) && !adminPreview ? (
        <SoldOut team={team}
                 csrfToken={csrfToken}
                 confirm={confirm}
                 ticketTypes={ticketTypes}
                 promoCode={promoCode}
                 ticketReservation={ticketReservation}
                 addOns={addOns}
                 nextStepIsChooseAddOns={nextStepIsChooseAddOns(ticketReservation, cachedVisibleAddOns, checkoutCategories)}
                 nextStepIsChooseDonation={nextStepIsChooseDonation(ticketReservation, cachedVisibleAddOns, donationCustomChargeType, checkoutCategories, confirm, userContext, ticketTypes, startAtDonation)}
                 visibleAddOns={cachedVisibleAddOns}
                 goToAddOns={goToAddOns}
                 goToDonation={() => { goToDonation(ticketReservation, ticketReservationChanged) }}
                 postToParent={postToParent}
                 ticketReservationChanged={ticketReservationChanged}
                 seatsIOSelectedObjects={seatsIOSelectedObjects}
                 ticketInsurance={ticketInsurance}
                 showHeader={showHeader}
                 waitlistEnabled={waitlistEnabled}
                 soldOutAddOns={cachedSoldOutAddOns}
                 userContext={userContext}
                 rootUrl={rootUrl}
                 onAuthSuccess={handleAuthSuccess} />
      ) : connectionLost && !ticketReservation.id && !adminPreview ? (
        <ConnectionLost team={team}
                        csrfToken={csrfToken}
                        confirm={confirm}
                        onRestart={onRestart} />
      ) : timeLimitReached && !adminPreview ? (
        <TimeLimitReached team={team}
                          csrfToken={csrfToken}
                          confirm={confirm}
                          onRestart={onRestart} />
      ) : !ticketReservation.id ? (
        <PickTickets team={team}
                     csrfToken={csrfToken}
                     confirm={confirm}
                     widgetType={widgetType}
                     adminPreview={adminPreview}
                     ticketTypes={ticketTypes}
                     promoCode={promoCode}
                     lookupPromoCode={lookupPromoCode}
                     promoCodeName={promoCodeName}
                     promoCodeNameChanged={promoCodeNameChanged}
                     promoCodeNameValid={promoCodeNameValid}
                     promoCodeInvalidReason={promoCodeInvalidReason}
                     promoCodeChanged={promoCodeChanged}
                     ticketReservation={ticketReservation}
                     ticketTypesChanged={ticketTypesChanged}
                     updateTicketTypeQuantity={updateTicketTypeQuantity}
                     buildTicketReservation={buildTicketReservation}
                     createTicketReservation={createTicketReservation}
                     customerioId={customerioId}
                     parentUrl={parentUrl}
                     visibleTicketTypes={boundVisibleTicketTypes}
                     visibleAddOns={cachedVisibleAddOns}
                     addOnQuantityOptions={addOnQuantityOptions}
                     addOns={addOns}
                     addOnsChanged={addOnsChanged}
                     ticketReservationChanged={ticketReservationChanged}
                     userContext={userContext}
                     cancelCheckout={handleCancelCheckout}
                     formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                     postToParent={postToParent}
                     seatsIOPublicKey={seatsIOPublicKey}
                     seatsIOChart={seatsIOChart}
                     seatsIOChartChanged={seatsIOChartChanged}
                     seatsIOSelectedObjects={seatsIOSelectedObjects}
                     seatsIOSelectedObjectsChanged={seatsIOSelectedObjectsChanged}
                     goToAddOns={goToAddOns}
                     goToDonation={() => { goToDonation(ticketReservation, ticketReservationChanged) }}
                     nextStepIsChooseAddOns={nextStepIsChooseAddOns(ticketReservation, cachedVisibleAddOns, checkoutCategories)}
                     nextStepIsChooseDonation={nextStepIsChooseDonation(ticketReservation, cachedVisibleAddOns, donationCustomChargeType, checkoutCategories, confirm, userContext, ticketTypes, startAtDonation)}
                     nextStepIsChooseMembership={nextStepIsChooseMembership(ticketReservation, cachedVisibleAddOns, donationCustomChargeType, checkoutCategories, confirm, userContext, ticketTypes, membershipProducts, startAtDonation)}
                     choosingAddOns={choosingAddOns}
                     choosingTicketTypes={choosingTicketTypes}
                     isCreatingTicketReservation={isCreatingTicketReservation}
                     cartStartBusyMessage={cartStartBusyMessage}
                     cartStartBusyMessageChanged={cartStartBusyMessageChanged}
                     ticketInsurance={ticketInsurance}
                     donationCustomChargeType={donationCustomChargeType}
                     donationCustomChargeTypeChanged={donationCustomChargeTypeChanged}
                     debouncedBuildTicketReservation={debouncedBuildTicketReservation}
                     feeRules={feeRules}
                     announceListEnabled={announceListEnabled}
                     showHeader={showHeader}
                     entryTimeLabel={entryTimeLabel}
                     seriesReturnUrl={seriesReturnUrl}
                     checkoutCategories={checkoutCategories}
                     ticketGroups={ticketGroups}
                     findTicketGroup={findTicketGroup}
                     waitlistEnabled={waitlistEnabled}
                     ticketTypesLoading={ticketTypesLoading}
                     startAtAddOns={startAtAddOns}
                     calculator={calculatorRef.current}
                     rootUrl={rootUrl}
                     onAuthSuccess={handleAuthSuccess}
                     soldOutAddOns={cachedSoldOutAddOns}
                     onPromoCodeRemoved={restoreOwnerPromoCodes}
                     membershipProducts={membershipProducts}
                     selectedMembershipProduct={selectedMembershipProduct}
                     selectedMembershipProductChanged={selectedMembershipProductChanged}
                     requestedCheckoutCategoryId={requestedCheckoutCategoryId}
                     goToMembership={() => { goToMembership(ticketReservation, ticketReservationChanged) }}
                     goBack={() => { goBack(ticketReservation, ticketReservationChanged) }}
                     resaleListings={resaleListings}
                     resaleOrderItems={resaleOrderItems}
                     resaleQuantityChanged={handleResaleQuantityChanged}
                     clearPromoRevealedTiers={clearPromoRevealedTiers}
                     />
      ) : ticketReservation.id ? (
        <Checkout team={team}
                  stripeLabelColor={widgetStyle && widgetStyle['--widget-font-color']}
                  useDefaultTheme={useDefaultTheme}
                  widgetTemplateId={widgetTemplateId}
                  widgetType={widgetType}
                  showCheckoutDate={showCheckoutDate}
                  csrfToken={csrfToken}
                  confirm={confirm}
                  ticketTypes={ticketTypes}
                  promoCode={promoCode}
                  ticketReservation={ticketReservation}
                  deleteTicketReservation={deleteTicketReservation}
                  stripePromise={stripePromise}
                  stripeError={stripeError}
                  stripeErrorChanged={stripeErrorChanged}
                  updateTicketReservation={updateTicketReservation}
                  rootUrl={rootUrl}
                  saveGeoCodedStateCode={saveGeoCodedStateCode}
                  checkoutTimePercentageRemaining={checkoutTimePercentageRemaining}
                  checkoutTimePercentageRemainingChanged={checkoutTimePercentageRemainingChanged}
                  timeLimitReachedChanged={timeLimitReachedChanged}
                  isPlacingOrder={isPlacingOrder}
                  isPlacingOrderChanged={isPlacingOrderChanged}
                  addOns={addOns}
                  gtag={gtag}
                  formatItemsForGoogleTagManager={formatItemsForGoogleTagManager}
                  customFieldValues={customFieldValues}
                  customFieldValueChanged={customFieldValueChanged}
                  postToParent={postToParent}
                  seatsIOSelectedObjects={seatsIOSelectedObjects}
                  autoSaveTicketReservation={autoSaveTicketReservation}
                  userContext={userContext}
                  verticalInsureClientID={verticalInsureClientID}
                  registerTicketInsurance={registerTicketInsurance}
                  insuranceOfferState={insuranceOfferState}
                  insuranceOfferStateChanged={insuranceOfferStateChanged}
                  ticketInsurance={ticketInsurance}
                  deleteTicketInsurance={deleteTicketInsurance}
                  hasInsuranceQuote={hasInsuranceQuote}
                  hasInsuranceQuoteChanged={hasInsuranceQuoteChanged}
                  ticketReservationChanged={ticketReservationChanged}
                  selectedMembershipProduct={selectedMembershipProduct}
                  resaleListings={resaleListings}
                  resaleOrderItems={resaleOrderItems}
                  defaultCountry={stripeConnectedAccountId ? 'CA' : null} />
      ) : null}
    </div>
  );
};

WebOrderForm.propTypes = {
  team: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
  confirm: PropTypes.object.isRequired,
  confirmChanged: PropTypes.func.isRequired,
  channelMsgReceived: PropTypes.func.isRequired,
  ticketTypes: PropTypes.array.isRequired,
  promoCode: PropTypes.object,
  lookupPromoCode: PropTypes.func.isRequired,
  promoCodeName: PropTypes.string,
  promoCodeNameChanged: PropTypes.func.isRequired,
  promoCodeNameValid: PropTypes.bool,
  promoCodeInvalidReason: PropTypes.string,
  promoCodeChanged: PropTypes.func.isRequired,
  ticketReservation: PropTypes.object,
  ticketTypesChanged: PropTypes.func.isRequired,
  buildTicketReservation: PropTypes.func.isRequired,
  ticketReservationChanged: PropTypes.func.isRequired,
  createTicketReservation: PropTypes.func.isRequired,
  deleteTicketReservation: PropTypes.func.isRequired,
  stripePromise: PropTypes.object,
  stripePromiseChanged: PropTypes.func.isRequired,
  stripePublishableKey: PropTypes.string.isRequired,
  stripeConnectedAccountId: PropTypes.string,
  stripeError: PropTypes.object,
  stripeErrorChanged: PropTypes.func.isRequired,
  updateTicketReservation: PropTypes.func.isRequired,
  rootUrl: PropTypes.string.isRequired,
  checkoutTimePercentageRemaining: PropTypes.number,
  checkoutTimePercentageRemainingChanged: PropTypes.func.isRequired,
  timeLimitReached: PropTypes.bool,
  timeLimitReachedChanged: PropTypes.func.isRequired,
  isPlacingOrder: PropTypes.bool,
  isPlacingOrderChanged: PropTypes.func.isRequired,
  addOns: PropTypes.array,
  addOnsChanged: PropTypes.func.isRequired,
  googleTrackingIds: PropTypes.array,
  gtag: PropTypes.func,
  gtagChanged: PropTypes.func.isRequired,
  customFieldValues: PropTypes.array,
  customFieldValueChanged: PropTypes.func.isRequired,
  userContext: PropTypes.object,
  cancelCheckout: PropTypes.func.isRequired,
  seatsIOPublicKey: PropTypes.string,
  seatsIOChart: PropTypes.object,
  seatsIOChartChanged: PropTypes.func.isRequired,
  seatsIOSelectedObjects: PropTypes.array,
  seatsIOSelectedObjectsChanged: PropTypes.func.isRequired,
  autoSaveTicketReservation: PropTypes.func.isRequired,
  customerioId: PropTypes.string.isRequired,
  customerioIdChanged: PropTypes.func.isRequired,
  parentUrl: PropTypes.string,
  parentUrlChanged: PropTypes.func.isRequired,
  reportParentUrlForUtmBeacon: PropTypes.func.isRequired,
  originAttributionChanged: PropTypes.func.isRequired,
  metaClickIdChanged: PropTypes.func.isRequired,
  attributionVisitorChanged: PropTypes.func,
  attributionVisitorUuid: PropTypes.string,
  isCreatingTicketReservation: PropTypes.bool,
  cartStartBusyMessage: PropTypes.string,
  cartStartBusyMessageChanged: PropTypes.func.isRequired,
  saveGeoCodedStateCode: PropTypes.func.isRequired,
  verticalInsureClientID: PropTypes.string,
  registerTicketInsurance: PropTypes.func.isRequired,
  insuranceOfferState: PropTypes.string,
  insuranceOfferStateChanged: PropTypes.func.isRequired,
  ticketInsurance: PropTypes.object,
  deleteTicketInsurance: PropTypes.func.isRequired,
  hasInsuranceQuote: PropTypes.bool,
  hasInsuranceQuoteChanged: PropTypes.func.isRequired,
  announceListEnabled: PropTypes.bool,
  waitlistEnabled: PropTypes.bool,
  donationCustomChargeType: PropTypes.object,
  donationCustomChargeTypeChanged: PropTypes.func.isRequired,
  feeRules: PropTypes.array.isRequired,
  startAtAddOns: PropTypes.bool,
  ticketsSoldElsewhere: PropTypes.bool,
  startAtDonation: PropTypes.bool,
  showHeader: PropTypes.bool,
  showCheckoutDate: PropTypes.bool,
  checkoutCategories: PropTypes.array,
  restrictedTicketTypeId: PropTypes.number,
  restrictedTicketGroupId: PropTypes.number,
  onRestart: PropTypes.func,
  entryTimeLabel: PropTypes.string,
  seriesReturnUrl: PropTypes.string,
  ticketGroups: PropTypes.object.isRequired,
  ticketGroupsChanged: PropTypes.func.isRequired,
  userContextChanged: PropTypes.func,
  membershipProducts: PropTypes.array,
  selectedMembershipProduct: PropTypes.object,
  fetchMembershipProducts: PropTypes.func.isRequired,
  fetchMembershipPromoCode: PropTypes.func.isRequired,
  fetchPassPromoCode: PropTypes.func.isRequired,
  selectedMembershipProductChanged: PropTypes.func.isRequired,
  resaleListings: PropTypes.array,
  resaleListingsFetched: PropTypes.func.isRequired,
  resaleOrderItems: PropTypes.array,
  resaleQuantityChanged: PropTypes.func.isRequired,
  clearPromoRevealedTiers: PropTypes.func.isRequired,
};

WebOrderForm.defaultProps = {
  promoCodeInvalidReason: null,
};

export default WebOrderForm;
